// -------------------------------------------------
// PRICING POLICY (FE) — the single source of truth on the frontend for
// VillaTracker's selling-price rules.
//
// TWIN of VTHub api/services/pricingPolicy.js. Both files MUST move in the same
// release: this one prices what the agent sees, that one prices the drill-in quote
// and the booking. If they drift, the card price and the quote disagree.
//
// THE MODEL (Tom + Moriya, 2026-07-17). We receive NET from every channel and we
// always push NET back (see pages/Reservations/EditReservation) — markup never
// leaves VillaTracker. Two tiers, keyed on the property's account:
//
//   | Account          | Markup on net | Reseller commission          | VT keeps    | Divider |
//   |------------------|---------------|------------------------------|-------------|---------|
//   | SHM (585a39db…)  |     +10%      | 10% of the total shown       |     -       | 1/1.10  |
//   | everything else  |     +22%      | 10% of the total shown       | 9.8% of net | 1/1.22  |
//
//   selling = net / divider = net * (1 + markup)
//
// SHM inventory is priced AT ORIGIN — Smiling House's margin is already inside the
// Guesty rate — so VT adds only the reseller's 10% and keeps nothing extra. RUDH G-
// twin accounts are NOT the SHM tier (SHM = the legacy Guesty accountId only).
// The 17% seen in marketing material is copy only — it prices nothing here.
//
// THE COMMISSION (Moriya's mail of 2 Nov 2026). The agent's commission is 10% of the total the
// agent sees, for every villa source and every account, SHM included (SH's own commission is
// already inside the SHM rate). It was 10% of net (8.2% of the total) until then, while the FAQ
// and the Reports page already said 10% of the booking value. The markups, and so every price,
// are unchanged.
//
// Before this module the rule was copy-pasted as bare literals (`/ 0.86`, `/ 10`)
// across the search page, the reserve payload and the property page — none of which
// tracked the real divider, so they silently drifted. Import from here instead of
// re-deriving a markup anywhere.
// -------------------------------------------------

export const SHM_ACCOUNT_ID = '585a39dbe43900100017e9e8';

// Kept as named parts (not baked literals) so the split stays legible: SHM rates
// already include SH's margin at origin, so VT's own cut there is 0.
export const AGENT_COMMISSION_RATE_NET = 0.10; // the reseller's part of the markup
export const AGENT_COMMISSION_RATE_OF_TOTAL = 0.10; // the reseller's commission: 10% of the total shown
const INTERNAL_MARKUP_SHM = 0;                 // margin already in the origin rate
const INTERNAL_MARKUP_STD = 0.12;              // our cut on everything else — hidden

export const SHM_DIVIDER = 1 / (1 + INTERNAL_MARKUP_SHM + AGENT_COMMISSION_RATE_NET); // 1/1.10
export const STD_DIVIDER = 1 / (1 + INTERNAL_MARKUP_STD + AGENT_COMMISSION_RATE_NET); // 1/1.22

// BookingPal is pinned to the standard tier rather than keyed on accountId: BP listings
// carry the property manager's own accountId, so a BP doc whose PM happened to be SHM
// would otherwise mis-resolve to the SHM pass-through tier. Twin of VTHub's BP_DIVIDER.
export const BP_DIVIDER = STD_DIVIDER;

// Lowest base price that still renders a NON-zero agent commission on a card. The card
// shows 10% of the marked-up base, more than round(base * AGENT_COMMISSION_RATE_NET), so
// only a base below 0.5/rate can round to "$0". Twin of VTHub's
// ZERO_COMMISSION_MIN_BASE (api/services/pricingPolicy.js) — the hubs use it to keep such
// listings out of search; here it triggers the calendar fallback for the no-dates card.
export const ZERO_COMMISSION_MIN_BASE = Math.ceil(0.5 / AGENT_COMMISSION_RATE_NET); // 5

/** Net → selling divider for a property's account. selling = net / divider. */
export function dividerFor(accountId) {
  return accountId === SHM_ACCOUNT_ID ? SHM_DIVIDER : STD_DIVIDER;
}

/** Selling price (what the customer pays) from a raw net amount. */
export function sellingFromNet(net, accountId) {
  const n = Number(net);
  if (!Number.isFinite(n)) return NaN; // caller decides; don't invent a 0 price
  return n / dividerFor(accountId);
}

/**
 * The agent's commission: 10% of the total the agent sees, on every villa.
 * Twin of VTHub's commissionOfTotal (api/services/pricingPolicy.js).
 */
export function commissionOfTotal(total) {
  const t = Number(total);
  if (!Number.isFinite(t)) return 0;
  return t * AGENT_COMMISSION_RATE_OF_TOTAL;
}

/**
 * The same commission from a NET amount (the reservation figures stay net): the commission
 * on the total that net sells at under `divider`.
 */
export function commissionFromNet(net, divider) {
  const n = Number(net);
  if (!Number.isFinite(n) || !(divider > 0)) return 0;
  return commissionOfTotal(n / divider);
}
